import React from 'react';
import { ExistsFunction, t, TFunction, TOptions } from 'i18next';
import {
  useTranslation,
  UseTranslationResponse,
  Translation,
} from 'react-i18next';
import { i18n } from './i18n';
import { setStoredLanguage } from './languageStorage';
import {
  Language,
  LANGUAGE_ENGLISH,
  LANGUAGE_ESTONIAN,
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
} from './types';

const addTranslations = (namespace: string, resources: Resources) => {
  const add = () => {
    for (const [language, translations] of Object.entries(resources)) {
      i18n.addResourceBundle(language, namespace, translations, true, false);
    }
  };
  if (!i18n.isInitialized) {
    i18n.on('initialized', add);
  } else {
    add();
  }
};

const initLanguages = async (lng: Language) => {
  if (i18n.language === LANGUAGE_LABELS) {
    await i18n.init({
      fallbackLng: [LANGUAGE_ESTONIAN],
      lng,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      appendNamespaceToCIMode: false,
    });
  }
};

const changeLanguage = async (language: Language) => {
  await initLanguages(language);
  await i18n.changeLanguage(language);
  await i18n.reloadResources(language);
  setStoredLanguage(language);
};

export const changeToEstonian = async () => {
  await changeLanguage(LANGUAGE_ESTONIAN);
};

export const changeToEnglish = async () => {
  await changeLanguage(LANGUAGE_ENGLISH);
};

export const changeToLabels = async () => {
  await i18n.init({
    fallbackLng: [LANGUAGE_LABELS],
    supportedLngs: [...SUPPORTED_LANGUAGES],
    appendNamespaceToCIMode: true,
  });
  await i18n.changeLanguage(LANGUAGE_LABELS);
  setStoredLanguage(LANGUAGE_LABELS);
};

export interface Resources {
  [language: string]: Translations;
}

export interface Translations {
  [key: string]: string | Translations;
}
export interface I18nNs<NS extends string> {
  addTranslations: (resources: Resources) => void;
  useTranslation: (key?: string) => UseTranslationResponse<string, string>;
  Translation: typeof Translation<NS>;
  t: TFunction<NS>;
  exists: ExistsFunction;
}

const i18nNsCache: string[] = [];

export const i18nNsFactory = <NS extends string>(namespace: NS): I18nNs<NS> => {
  if (i18nNsCache.includes(namespace)) {
    console.warn(
      'Namespace "%s" is registered multiple times with i18nNsCache(). Translations with same namespace are merged.',
      namespace,
    );
  }
  i18nNsCache.push(namespace);

  return {
    useTranslation: (key?: string) => {
      if (!i18n.isInitialized) {
        throw new Error(
          'I18n is not initialized. Did you forget to implement I18nProvider wrapper?',
        );
      }
      return useTranslation(namespace, { keyPrefix: key });
    },
    addTranslations: (resources: Resources) =>
      addTranslations(namespace, resources),
    Translation: (props) => <Translation ns={namespace} {...props} />,
    /**
     * This is a wrapper around i18next.t function that adds namespace to the options.
     * Provides the opportunity to translate phrases outside of React components but with the caveat:
     * If called prematurely (before i18n is initialized) it will throw an error.
     *
     * @example
     *
     * Won't work! t function is not ready to be consumed on root-level because i18n-next is not synchronously initialized during bootstrapping
     * ```
     * import { i18nNsFactory } from '@atlas-frontend/i18n';
     * const { t } from i18nNsFactory('my-namespace');
     * const myTranslatedPhrase = t('my.translated.phrase');
     * ```
     *
     * Instead, use it inside a function which is called on-demand after i18n-next initialization (inside React UI component that is descendant of <I18nProvider>).
     * ```
     * import { i18nNsFactory } from '@atlas-frontend/i18n';
     * const { t } from i18nNsFactory('my-namespace');
     * const translateMyPhrase = () => t('my.translated.phrase');
     * ```
     */
    t: ((...args) => {
      if (!i18n.isInitialized) {
        throw new Error(
          'I18n is not initialized. Consume t() (directly or indirectly) only from a React UI component that is descendant of <I18nProvider>.',
        );
      }

      if (args.length < 2) {
        return t(args[0], { ns: namespace });
      }

      const lastArgument = args.pop();

      if (typeof lastArgument === 'string') {
        return t(args[0] as string | string[], lastArgument, { ns: namespace });
      }

      const options: TOptions = { ...lastArgument, ns: namespace };

      return args.length === 3
        ? t(args[0], args[1], options)
        : t(args[0], options);
    }) as TFunction<NS>,
    exists: (keys, options) => i18n.exists(keys, { ...options, ns: namespace }),
  };
};
